import type {
  FabricSpecsModelSnapshot,
  SpecsCatalogFabric,
  SpecsCatalogFabricColor,
} from "../types";
import type { SpecsAutoFormFieldMetadataMap } from "../autoFormMetadata";
import { ObservableModelNode } from "../observable";
import {
  cloneValue,
  expectObject,
  readObjectArray,
  readString,
  splitKnown,
} from "../utils";
import { SpecsFabricColorModel } from "./SpecsFabricColorModel";

export class SpecsFabricModel extends ObservableModelNode {
  static autoFormFields: SpecsAutoFormFieldMetadataMap = {
    name: { label: "Name", fieldType: "text" },
    group: { label: "Group", fieldType: "text" },
    factory: { label: "Factory", fieldType: "text" },
    description: { label: "Description", fieldType: "text" },
    fabricLot: { label: "Fabric lot", fieldType: "text" },
    textColor: { label: "Text color", fieldType: "color" },
    extra: { label: "Extra JSON", fieldType: "json", minRows: 8 },
  };

  name!: string;
  group!: string;
  factory!: string;
  description!: string;
  fabricLot!: string;
  textColor!: string;
  defaultColor!: SpecsFabricColorModel | null;
  colors!: SpecsFabricColorModel[];
  extra!: Record<string, unknown>;
  private hasGroup = false;
  private hasFactory = true;
  private hasDescription = true;
  private hasFabricLot = false;
  private hasTextColor = false;

  constructor() {
    super();
    this.defineObservableValue("name", "");
    this.defineObservableValue("group", "");
    this.defineObservableValue("factory", "AT");
    this.defineObservableValue("description", "");
    this.defineObservableValue("fabricLot", "");
    this.defineObservableValue("textColor", "");
    this.defineObservableValue("defaultColor", null);
    this.defineObservableValue("colors", []);
    this.defineObservableValue("extra", {});
  }

  static getColors(fabric: SpecsCatalogFabric): SpecsCatalogFabricColor[] {
    return [
      ...(fabric.defaultColor ? [fabric.defaultColor] : []),
      ...(fabric.colors ?? []),
    ];
  }

  static getColorValue(color: SpecsCatalogFabricColor) {
    return color.generatorHex || color.hex || "";
  }

  static findColorByName(
    fabrics: SpecsCatalogFabric[],
    colorName: string | null | undefined
  ) {
    const nameKey = colorName?.trim().toLowerCase();
    if (!nameKey) {
      return null;
    }

    for (const fabric of fabrics) {
      const color = SpecsFabricModel.getColors(fabric).find(
        (candidate) => candidate.name?.trim().toLowerCase() === nameKey
      );
      if (color) {
        return color;
      }
    }

    return null;
  }

  static findColorByStoredValue(fabrics: SpecsCatalogFabric[], value: string) {
    const valueKey = value.trim().toLowerCase();
    if (!valueKey) {
      return null;
    }

    for (const fabric of fabrics) {
      const color = SpecsFabricModel.getColors(fabric).find((candidate) => {
        return (
          candidate.hex?.trim().toLowerCase() === valueKey ||
          candidate.generatorHex?.trim().toLowerCase() === valueKey
        );
      });
      if (color) {
        return color;
      }
    }

    return null;
  }

  restore(snapshot: unknown) {
    const source = expectObject(snapshot, "fabric");
    this.hasGroup = "group" in source;
    this.hasFactory = "factory" in source;
    this.hasDescription = "description" in source;
    this.hasFabricLot = "fabricLot" in source;
    this.hasTextColor = "textColor" in source;
    this.name = readString(source, "name");
    this.group = readString(source, "group");
    this.factory = readString(source, "factory");
    this.description = readString(source, "description");
    this.fabricLot = readString(source, "fabricLot");
    this.textColor = readString(source, "textColor");
    this.defaultColor =
      source.defaultColor &&
      typeof source.defaultColor === "object" &&
      !Array.isArray(source.defaultColor)
        ? new SpecsFabricColorModel().restore(source.defaultColor)
        : null;
    this.colors = readObjectArray(source, "colors").map((color) =>
      new SpecsFabricColorModel().restore(color)
    );
    this.extra = splitKnown(source, [
      "name",
      "group",
      "factory",
      "description",
      "fabricLot",
      "textColor",
      "defaultColor",
      "colors",
    ]);
    return this;
  }

  save(): FabricSpecsModelSnapshot {
    const saved: FabricSpecsModelSnapshot = {
      ...cloneValue(this.extra),
      name: this.name,
      colors: this.colors.map((color) => color.save()),
    };

    if (this.hasGroup || this.group) {
      saved.group = this.group;
    }
    if (this.hasFactory || this.factory) {
      saved.factory = this.factory;
    }
    if (this.hasDescription || this.description) {
      saved.description = this.description;
    }
    if (this.hasFabricLot || this.fabricLot) {
      saved.fabricLot = this.fabricLot;
    }
    if (this.hasTextColor || this.textColor) {
      saved.textColor = this.textColor;
    }
    if (this.defaultColor) {
      saved.defaultColor = this.defaultColor.save();
    }

    return saved;
  }
}
